import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiArrowLeft,
  FiSearch,
  FiHelpCircle,
  FiChevronRight,
  FiX,
} from "react-icons/fi";

import logo1 from "../Assets/phantom.svg";
import Backpack from "../Assets/logo-36.png";
import Solflare from "../Assets/Solflare.jpeg";
import logo3 from "../Assets/logo3.png";
import Rainbow from "../Assets/wall52.webp";
import Coinbase from "../Assets/logo4.png";
import MetaMask from "../Assets/wall7.webp";
import Ledger from "../Assets/ledger.png";

const wallets = [
  { name: "MetaMask", logo: MetaMask, recommended: true },
  { name: "Coinbase", logo: Coinbase, recommended: true },
  { name: "Rabby", logo: Backpack, recommended: true },
  { name: "Phantom", logo: logo1, recommended: true },
  { name: "WalletConnect", logo: logo3, alt: true },
  { name: "Ledger", logo: Ledger },
  { name: "Solflare", logo: Solflare },
  { name: "Rainbow", logo: Rainbow },
];

export default function ConnectWalletModal({
  isOpen,
  onClose,
  setShowModal2,
  setShowModal3,
}) {
  const [searchQuery, setSearchQuery] = useState("");

  const filteredWallets = wallets.filter((w) =>
    w.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/80 backdrop-blur-sm">
          {/* Backdrop Overlay */}
          <motion.div
            className="absolute inset-0"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          {/* Modal Container:
              Mobile -> Bottom Sheet Drawer (items-end, rounded-t-2xl)
              Tablet & Desktop -> Centered Modal (rounded-2xl)
          */}
          <motion.div
            initial={{ y: "100%", opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: "100%", opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="relative w-full sm:max-w-md md:max-w-lg bg-[#121318] text-white rounded-t-[28px] sm:rounded-2xl p-4 sm:p-6 shadow-2xl z-10 font-sans border border-[#1e2029] flex flex-col max-h-[85vh] sm:max-h-[80vh]"
          >
            {/* Mobile Touch Drag Handle Indicator */}
            <div className="w-10 h-1 mx-auto mb-3 bg-gray-700 rounded-full sm:hidden" />

            {/* Modal Header */}
            <div className="flex items-center justify-between pb-3 sm:pb-4 border-b border-[#1c1e26]">
              <div className="flex items-center gap-3">
                <button
                  onClick={onClose}
                  className="p-1.5 text-gray-400 hover:text-white rounded-lg transition-colors hover:bg-[#1a1c24]"
                >
                  <FiArrowLeft size={18} />
                </button>
                <h2 className="text-base font-bold tracking-tight text-white sm:text-lg">
                  Select your wallet
                </h2>
              </div>

              <div className="flex items-center gap-2">
                <button className="p-1.5 text-gray-400 hover:text-white transition-colors">
                  <FiHelpCircle size={18} />
                </button>
                <button
                  onClick={onClose}
                  className="p-1.5 text-gray-400 hover:text-white sm:hidden"
                >
                  <FiX size={18} />
                </button>
              </div>
            </div>

            {/* Search Input Bar */}
            <div className="relative my-3 sm:my-4">
              <FiSearch
                size={18}
                className="absolute text-gray-400 left-3.5 top-1/2 -translate-y-1/2"
              />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search through 586 wallets..."
                className="w-full bg-[#1b1d26] border border-[#262936] rounded-xl py-2.5 sm:py-3 pl-10 pr-4 text-xs sm:text-sm font-medium text-white placeholder-gray-500 outline-none focus:border-indigo-500 transition-all"
              />
            </div>

            {/* Wallet List Items */}
            <div className="flex-1 pr-1 space-y-1 overflow-y-auto custom-scrollbar">
              {filteredWallets.map((wallet, i) => (
                <button
                  key={i}
                  onClick={() => {
                    onClose();
                    if (wallet?.alt) {
                      setShowModal3?.(true);
                    } else {
                      setShowModal2?.({
                        name: wallet.name,
                        icon: wallet.logo,
                      });
                    }
                  }}
                  className="w-full flex items-center justify-between p-3 rounded-xl hover:bg-[#1b1d26] transition-colors group text-left"
                >
                  {/* Left Logo & Name */}
                  <div className="flex items-center gap-3.5">
                    <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl overflow-hidden bg-[#181a22] border border-[#262936] p-1 flex items-center justify-center shrink-0">
                      <img
                        src={wallet.logo}
                        alt={wallet.name}
                        className="object-contain w-full h-full rounded-full"
                      />
                    </div>
                    <span className="text-sm font-semibold text-white transition-colors sm:text-base group-hover:text-indigo-400">
                      {wallet.name}
                    </span>
                  </div>

                  {/* Right Recommended Tag & Chevron */}
                  <div className="flex items-center gap-2">
                    {wallet.recommended && (
                      <span className="text-[10px] sm:text-[11px] font-bold text-gray-400 tracking-wider uppercase">
                        RECOMMENDED
                      </span>
                    )}
                    <FiChevronRight
                      size={16}
                      className="text-gray-500 transition-colors group-hover:text-white"
                    />
                  </div>
                </button>
              ))}

              {filteredWallets.length === 0 && (
                <div className="py-8 text-xs text-center text-gray-500">
                  No wallets found matching "{searchQuery}"
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
